import { Component } from '@theme/component';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { formatShopMoney } from '@theme/money-formatting';
import { isMobileBreakpoint, prefersReducedMotion, yieldToMainThread } from '@theme/utilities';
/**
 * @typedef {{
 *   grid: HTMLElement,
 *   filters?: HTMLElement,
 *   footer?: HTMLElement,
 *   loadMore?: HTMLButtonElement,
 * }} Refs
 */

/**
 * @typedef {{
 *   id: number,
 *   available: boolean,
 *   price: number,
 *   compare: number,
 *   discount: number,
 *   option: string,
 *   image: string | null,
 * }} NurseryVariant
 */

/**
 * @typedef {{
 *   selectedId: number,
 *   variants: NurseryVariant[],
 * }} NurseryProductData
 */

/**
 * @typedef {{
 *   positions: Map<HTMLElement, DOMRect>,
 *   height: number,
 * }} MasonryLayout
 */

/**
 * Nursery Inspiration gallery: masonry grid, style filters, room dialog, and bulk cart add.
 *
 * @extends {Component<Refs>}
 */
class NurseryInspirationComponent extends Component {
    /** @type {string[]} */
    requiredRefs = ['grid'];

    /** @type {AbortController | null} */
    #abortController = null;

    /** @type {ResizeObserver | null} */
    #resizeObserver = null;

    /** @type {HTMLElement[]} */
    #rooms = [];

    /** @type {number} */
    #activeIndex = 0;

    /** @type {string} */
    #activeFilter = '';

    /** @type {number} */
    #visibleLimit = 0;

    /** @type {number | null} */
    #ctaResetTimeout = null;

    /** @type {number | null} */
    #dialogFitFrame = null;

    connectedCallback() {
        super.connectedCallback();
        this.#abortController = new AbortController();
        const { signal } = this.#abortController;

        this.#rooms = Array.from(this.querySelectorAll('.nni__room'));
        this.#visibleLimit = this.#parseInitialCount();

        document.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });
        document.addEventListener('shopify:block:deselect', this.#onBlockDeselect, { signal });
        document.addEventListener('keydown', this.#onKeyDown, { signal });
        window.addEventListener('resize', this.#scheduleDialogFit, { signal });
        this.#dialogHost()?.addEventListener(DialogCloseEvent.eventName, this.#onDialogClosed, { signal });
        this.addEventListener('click', this.#onHostClick, { signal });

        this.#resizeObserver = new ResizeObserver(() => this.#layoutMasonry());
        this.#resizeObserver.observe(this.refs.grid);

        if (Shopify.designMode) this.#visibleLimit = this.#rooms.length;

        this.#hydrateFilters();
        this.#applyDeepLink();
        this.#syncVisibility();
        this.#bindTileImages();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.#abortController?.abort();
        this.#resizeObserver?.disconnect();
        if (this.#ctaResetTimeout !== null) window.clearTimeout(this.#ctaResetTimeout);
        if (this.#dialogFitFrame !== null) window.cancelAnimationFrame(this.#dialogFitFrame);
    }

    updatedCallback() {
        this.#rooms = Array.from(this.querySelectorAll('.nni__room'));
        if (Shopify.designMode) this.#visibleLimit = this.#rooms.length;
        this.#hydrateFilters();
        this.#syncVisibility();
        this.#bindTileImages();
    }

    /**
     * @param {Event} event
     */
    openRoomFromTile(event) {
        const target = event.target;
        if (!(target instanceof Element)) return;
        const room = target.closest('.nni__room');
        if (!(room instanceof HTMLElement)) return;
        this.#openRoom(room);
    }

    showPreviousRoom() {
        this.#stepRoom(-1);
    }

    showNextRoom() {
        this.#stepRoom(1);
    }

    /**
     * @param {Event} event
     */
    scrollProducts(event) {
        const control = event.target;
        const products = this.querySelector('[data-nni-products]');
        if (!(control instanceof HTMLInputElement) || !(products instanceof HTMLElement)) return;
        products.scrollLeft = Number(control.value);
    }

    syncProductScroll() {
        this.#syncProductScroll();
    }

    /**
     * @param {Event} event
     */
    async filterRooms(event) {
        const target = event.target;
        if (!(target instanceof Element)) return;
        const button = target.closest('.nni__filter');
        if (!(button instanceof HTMLButtonElement)) return;
        const nextFilter = button.dataset.styleFilter || '';
        if (nextFilter === this.#activeFilter) return;
        const previousLayout = this.#captureMasonryLayout();
        await yieldToMainThread();
        this.#activeFilter = nextFilter;
        this.#visibleLimit = this.#parseInitialCount();
        this.#syncFilterButtons();
        this.#syncVisibility(previousLayout);
    }

    loadMore() {
        const previousLayout = this.#captureMasonryLayout();
        this.#visibleLimit = this.#rooms.length;
        this.#syncVisibility(previousLayout);
    }

    async shopTheRoom() {
        const cta = this.querySelector('[data-nni-cta]');
        if (!(cta instanceof HTMLButtonElement) || cta.getAttribute('aria-disabled') === 'true' || cta.disabled) {
            return;
        }

        const items = this.#selectedItems();
        if (!items.length) return;

        const status = this.querySelector('[data-nni-cta-status]');
        const alert = this.querySelector('[data-nni-cta-alert]');
        if (alert instanceof HTMLElement) alert.textContent = '';

        cta.setAttribute('aria-disabled', 'true');
        const addingText = this.dataset.addingText || '';
        cta.textContent = addingText;
        if (status instanceof HTMLElement) status.textContent = addingText;

        try {
            const response = await fetch(Theme.routes.cart_add_url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ items }),
            });

            if (!response.ok) throw new Error(`Room add to cart failed: ${response.status}`);
            await response.json();
            const cart = await this.#fetchCart();

            document.dispatchEvent(
                new CartAddEvent(cart, this.id, {
                    source: 'na-nursery-inspiration',
                    itemCount: cart.item_count,
                })
            );

            const addedText = this.dataset.addedText || '';
            cta.textContent = addedText;
            if (status instanceof HTMLElement) status.textContent = addedText;
        } catch (error) {
            console.error(error);
            const errorText = this.dataset.errorText || '';
            cta.textContent = errorText;
            if (status instanceof HTMLElement) status.textContent = '';
            if (alert instanceof HTMLElement) alert.textContent = errorText;
        } finally {
            if (this.#ctaResetTimeout !== null) window.clearTimeout(this.#ctaResetTimeout);
            this.#ctaResetTimeout = window.setTimeout(() => {
                cta.replaceChildren();
                const label = document.createElement('span');
                label.setAttribute('data-nni-cta-label', '');
                const price = document.createElement('span');
                price.setAttribute('data-nni-cta-price', '');
                cta.append(label, price);
                this.#renderTotals();
                cta.removeAttribute('aria-disabled');
                cta.disabled = !this.#canShopRoom();
                this.#ctaResetTimeout = null;
            }, 2000);
        }
    }

    #parseInitialCount() {
        const value = Number(this.dataset.initialCount);
        return Number.isFinite(value) && value > 0 ? value : this.#rooms.length;
    }

    #columnCount() {
        const desktop = Number(this.dataset.columnsDesktop) || 3;
        const mobile = Number(this.dataset.columnsMobile) || 2;
        return isMobileBreakpoint() ? mobile : desktop;
    }

    #bindTileImages() {
        this.refs.grid.querySelectorAll('.nni__tile-image').forEach((image) => {
            if (!(image instanceof HTMLImageElement)) return;
            if (image.complete) return;
            image.addEventListener('load', () => this.#layoutMasonry(), { once: true });
        });
    }

    #matchingRooms() {
        if (!this.#activeFilter) return this.#rooms;
        return this.#rooms.filter((room) => {
            return room.dataset.styleSlug === this.#activeFilter || room.dataset.style === this.#activeFilter;
        });
    }

    #hydrateFilters() {
        const nav = this.refs.filters;
        if (!(nav instanceof HTMLElement)) return;
        if (nav.querySelectorAll('.nni__filter[data-style-filter]:not([data-style-filter=""])').length) return;

        /** @type {{ name: string, slug: string }[]} */
        const styles = [];
        const addStyle = (name, slug) => {
            if (!name || styles.some((style) => style.slug === slug || style.name === name)) return;
            styles.push({ name, slug: slug || this.#slugify(name) });
        };

        this.querySelectorAll('.nni__filter-source').forEach((source) => {
            if (!(source instanceof HTMLElement)) return;
            addStyle(source.dataset.styleName || source.textContent?.trim() || '', source.dataset.styleFilter || '');
        });

        if (!styles.length) {
            this.#rooms.forEach((room) => {
                addStyle(room.dataset.style || '', room.dataset.styleSlug || '');
            });
        }

        styles.forEach((style) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'nni__filter';
            button.dataset.styleFilter = style.slug;
            button.dataset.styleName = style.name;
            button.setAttribute('aria-pressed', 'false');
            button.textContent = style.name;
            nav.append(button);
        });
    }

    /**
     * @param {string} value
     */
    #slugify(value) {
        return value
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '');
    }

    /** @param {MouseEvent} event */
    #onHostClick = (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const filter = target.closest('.nni__filter');
        if (filter instanceof HTMLButtonElement && this.contains(filter)) {
            this.filterRooms(event);
            return;
        }

        const tile = target.closest('.nni__tile');
        if (tile instanceof HTMLElement && this.contains(tile)) {
            this.openRoomFromTile(event);
        }
    };

    #syncFilterButtons() {
        this.querySelectorAll('.nni__filter').forEach((button) => {
            if (!(button instanceof HTMLButtonElement)) return;
            const active = (button.dataset.styleFilter || '') === this.#activeFilter;
            button.classList.toggle('is-active', active);
            button.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
    }

    /**
     * @param {MasonryLayout} [previousLayout]
     */
    #syncVisibility(previousLayout) {
        const matches = this.#matchingRooms();
        let shown = 0;

        this.#rooms.forEach((room) => {
            const isMatch = matches.includes(room);
            const deferred = isMatch && shown >= this.#visibleLimit;
            if (isMatch && !deferred) shown += 1;
            room.classList.toggle('is-filtered', !isMatch);
            room.classList.toggle('is-deferred', deferred);
        });

        const hasMore = shown < matches.length;
        this.classList.toggle('is-collapsed', hasMore);
        this.refs.loadMore?.toggleAttribute('hidden', !hasMore);
        this.refs.footer?.toggleAttribute('hidden', !hasMore);
        this.#layoutMasonry();
        if (previousLayout) this.#animateMasonry(previousLayout);
    }

    /**
     * @returns {HTMLElement[]}
     */
    #visibleMasonryItems() {
        const grid = this.refs.grid;
        /** @type {HTMLElement[]} */
        const items = [];

        Array.from(grid.children).forEach((child) => {
            if (!(child instanceof HTMLElement) || child.hidden) return;
            if (child.classList.contains('nni__filter-source') || child.querySelector('.nni__filter-source')) {
                return;
            }
            const room = child.matches('.nni__room') ? child : child.querySelector('.nni__room');
            if (!(room instanceof HTMLElement)) return;
            if (room.classList.contains('is-filtered') || room.classList.contains('is-deferred')) return;
            items.push(child);
        });

        return items;
    }

    /**
     * @returns {MasonryLayout}
     */
    #captureMasonryLayout() {
        const grid = this.refs.grid;
        /** @type {Map<HTMLElement, DOMRect>} */
        const positions = new Map();
        const height = grid.getBoundingClientRect().height;

        grid.getAnimations().forEach((animation) => animation.cancel());
        this.#visibleMasonryItems().forEach((item) => {
            positions.set(item, item.getBoundingClientRect());
            item.getAnimations().forEach((animation) => animation.cancel());
        });

        return { positions, height };
    }

    /**
     * @param {MasonryLayout} previousLayout
     */
    #animateMasonry(previousLayout) {
        if (prefersReducedMotion()) return;

        const grid = this.refs.grid;
        const items = this.#visibleMasonryItems();
        const styles = getComputedStyle(this);
        const speed = styles.getPropertyValue('--animation-speed-slow').trim();
        const parsedSpeed = Number.parseFloat(speed);
        const duration = Number.isFinite(parsedSpeed) ? parsedSpeed * (speed.endsWith('ms') ? 1 : 1000) : 200;
        const easing = styles.getPropertyValue('--ease-out-cubic').trim() || 'ease-out';
        const previousPositions = Array.from(previousLayout.positions.values());
        const nextHeight = grid.getBoundingClientRect().height;

        if (Math.abs(previousLayout.height - nextHeight) > 1) {
            const heightAnimation = grid.animate(
                [{ height: `${previousLayout.height}px` }, { height: `${nextHeight}px` }],
                { duration, easing }
            );
            heightAnimation.addEventListener('finish', () => heightAnimation.cancel(), { once: true });
        }

        items.forEach((item, index) => {
            const currentPosition = item.getBoundingClientRect();
            const retainedPosition = previousLayout.positions.get(item);
            const previousPosition = retainedPosition || previousPositions[index % previousPositions.length];
            const offsetX = previousPosition ? previousPosition.left - currentPosition.left : 0;
            const offsetY = previousPosition ? previousPosition.top - currentPosition.top : 0;
            const isEntering = !retainedPosition;

            if (!isEntering && Math.abs(offsetX) <= 1 && Math.abs(offsetY) <= 1) return;

            const itemAnimation = item.animate(
                [
                    {
                        opacity: isEntering ? 0 : 1,
                        transform: `translate(${offsetX}px, ${offsetY}px) scale(${isEntering ? 0.98 : 1})`,
                    },
                    { opacity: 1, transform: 'translate(0, 0) scale(1)' },
                ],
                { duration, easing }
            );
            itemAnimation.addEventListener('finish', () => itemAnimation.cancel(), { once: true });
        });
    }

    #layoutMasonry() {
        const grid = this.refs.grid;
        const items = this.#visibleMasonryItems();
        const columns = this.#columnCount();
        const styles = getComputedStyle(this);
        const gapProperty = isMobileBreakpoint() ? '--nni-gap-mobile' : '--nni-gap';
        const gap = Number.parseFloat(styles.getPropertyValue(gapProperty)) || 24;
        const width = grid.clientWidth;
        if (width <= 0 || items.length === 0) {
            grid.style.height = '';
            return;
        }

        const columnWidth = (width - gap * (columns - 1)) / columns;

        Array.from(grid.children).forEach((child) => {
            if (!(child instanceof HTMLElement)) return;
            if (items.includes(child)) return;
            child.style.position = '';
            child.style.width = '';
            child.style.left = '';
            child.style.top = '';
        });

        items.forEach((item) => {
            item.style.position = 'absolute';
            item.style.width = `${columnWidth}px`;
        });

        /** @type {number[]} */
        const heights = Array.from({ length: columns }, () => 0);
        const itemHeights = items.map((item) => item.offsetHeight);
        const placements = items.map((item, index) => {
            const column = heights.indexOf(Math.min(...heights));
            const safeColumn = column === -1 ? 0 : column;
            const top = heights[safeColumn] || 0;
            heights[safeColumn] = top + (itemHeights[index] || 0) + gap;
            return { item, left: safeColumn * (columnWidth + gap), top };
        });

        placements.forEach(({ item, left, top }) => {
            item.style.left = `${left}px`;
            item.style.top = `${top}px`;
        });

        grid.style.height = `${Math.max(0, ...heights) - (items.length ? gap : 0)}px`;
    }

    /**
     * @param {HTMLElement} room
     */
    #openRoom(room) {
        const index = this.#rooms.indexOf(room);
        if (index < 0) return;
        this.#activeIndex = index;
        this.#renderActiveRoom();
        const host = this.#dialogHost();
        if (host) {
            host.showDialog();
            this.#scheduleDialogFit();
            return;
        }
        const dialog = this.querySelector('dialog');
        if (dialog instanceof HTMLDialogElement && !dialog.open) {
            dialog.showModal();
            this.#scheduleDialogFit();
        }
    }

    /**
     * @param {number} step
     */
    #stepRoom(step) {
        if (!this.#rooms.length) return;
        const next = (this.#activeIndex + step + this.#rooms.length) % this.#rooms.length;
        const room = this.#rooms[next];
        if (!room) return;
        this.#activeIndex = next;
        this.#renderActiveRoom();
    }

    #renderActiveRoom() {
        const room = this.#rooms[this.#activeIndex];
        if (!room) return;
        const template = room.querySelector('.nni__room-template');
        const mediaHost = this.querySelector('[data-nni-media]');
        const productsHost = this.querySelector('[data-nni-products]');
        const previousPreviewHost = this.querySelector('[data-nni-preview-prev]');
        const nextPreviewHost = this.querySelector('[data-nni-preview-next]');
        const title = this.querySelector('[data-nni-title]');
        if (!(template instanceof HTMLTemplateElement) || !mediaHost || !productsHost) return;

        const clone = template.content.cloneNode(true);
        if (!(clone instanceof DocumentFragment)) return;

        mediaHost.replaceChildren();
        productsHost.replaceChildren();
        if (productsHost instanceof HTMLElement) productsHost.scrollLeft = 0;
        const media = clone.querySelector('.nni-room-media');
        const products = clone.querySelector('.nni-room-products');
        const roomImage = media?.querySelector('.nni-room-media__image');
        if (roomImage instanceof HTMLImageElement) {
            roomImage.loading = 'eager';
            roomImage.fetchPriority = 'high';
        }
        if (media) mediaHost.append(media);
        if (products) productsHost.append(products);

        const previousIndex = (this.#activeIndex - 1 + this.#rooms.length) % this.#rooms.length;
        const nextIndex = (this.#activeIndex + 1) % this.#rooms.length;
        this.#renderRoomPreview(previousPreviewHost, this.#rooms[previousIndex]);
        this.#renderRoomPreview(nextPreviewHost, this.#rooms[nextIndex]);

        if (title) title.textContent = room.dataset.style || '';
        this.#bindMediaLoading(mediaHost);
        this.#bindHotspot(mediaHost);
        this.#bindProductRows(productsHost);
        this.#renderTotals();
        this.#restartEntrance(mediaHost);
        this.#restartEntrance(productsHost);
        if (this.#dialogHost()?.refs.dialog.open) this.#scheduleDialogFit();
    }

    /**
     * @param {Element} mediaHost
     */
    #bindMediaLoading(mediaHost) {
        const media = mediaHost.querySelector('.nni-room-media');
        const image = media?.querySelector('.nni-room-media__image');
        if (!(media instanceof HTMLElement) || !(image instanceof HTMLImageElement)) {
            mediaHost.removeAttribute('aria-busy');
            return;
        }

        let settled = false;
        mediaHost.setAttribute('aria-busy', 'true');

        const reveal = async () => {
            if (settled) return;
            try {
                await image.decode();
            } catch {}
            if (settled) return;
            if (!mediaHost.contains(media)) return;
            settled = true;
            media.classList.add('is-loaded');
            mediaHost.removeAttribute('aria-busy');
            this.#scheduleDialogFit();
        };

        image.addEventListener('load', reveal, { once: true });
        image.addEventListener('error', reveal, { once: true });
        if (image.complete) void reveal();
    }

    /**
     * @param {Element | null} host
     * @param {HTMLElement | undefined} room
     */
    #renderRoomPreview(host, room) {
        if (!(host instanceof HTMLElement)) return;
        const source = room?.dataset.overlaySrc;
        if (!source) {
            host.replaceChildren();
            return;
        }

        const image = new Image();
        image.className = 'nni-dialog__peek-image';
        image.alt = '';
        image.width = 1050;
        image.height = 583;
        image.decoding = 'async';
        image.src = source;
        host.replaceChildren(image);
    }

    /**
     * @param {Element} element
     */
    #restartEntrance(element) {
        if (prefersReducedMotion()) return;
        element.classList.remove('is-entering');
        void element.clientWidth;
        element.classList.add('is-entering');
        element.addEventListener('animationend', () => element.classList.remove('is-entering'), { once: true });
    }

    #scheduleDialogFit = () => {
        if (this.#dialogFitFrame !== null) window.cancelAnimationFrame(this.#dialogFitFrame);
        this.#dialogFitFrame = window.requestAnimationFrame(() => {
            this.#dialogFitFrame = window.requestAnimationFrame(() => {
                this.#dialogFitFrame = null;
                this.#fitDialogMedia();
            });
        });
    };

    #fitDialogMedia() {
        const dialog = this.#dialogHost()?.refs.dialog;
        const mediaHost = this.querySelector('[data-nni-media]');
        const image = mediaHost?.querySelector('.nni-room-media__image');
        if (
            !(dialog instanceof HTMLDialogElement) ||
            !dialog.open ||
            !(mediaHost instanceof HTMLElement) ||
            !(image instanceof HTMLImageElement)
        ) {
            return;
        }

        mediaHost.style.removeProperty('width');
        this.#syncProductScroll();
        if (isMobileBreakpoint()) return;

        const overflow = dialog.scrollHeight - dialog.clientHeight;
        if (overflow <= 0) return;

        const imageWidth = image.naturalWidth || Number(image.getAttribute('width'));
        const imageHeight = image.naturalHeight || Number(image.getAttribute('height'));
        const renderedHeight = image.getBoundingClientRect().height;
        if (!imageWidth || !imageHeight || !renderedHeight) return;

        const availableHeight = Math.max(0, renderedHeight - overflow - 1);
        const fittedWidth = availableHeight * (imageWidth / imageHeight);
        mediaHost.style.width = `${Math.min(mediaHost.getBoundingClientRect().width, fittedWidth)}px`;
    }

    #syncProductScroll() {
        const products = this.querySelector('[data-nni-products]');
        const control = this.querySelector('[data-nni-product-scroll]');
        if (!(products instanceof HTMLElement) || !(control instanceof HTMLInputElement)) return;

        const maxScroll = Math.max(0, products.scrollWidth - products.clientWidth);
        if (isMobileBreakpoint() || maxScroll <= 1) {
            products.scrollLeft = 0;
            control.hidden = true;
            control.value = '0';
            control.style.removeProperty('--nni-product-scroll-thumb-size');
            return;
        }

        const thumbSize = Math.max(0, (products.clientWidth * products.clientWidth) / products.scrollWidth);
        control.hidden = false;
        control.max = String(Math.ceil(maxScroll));
        control.value = String(Math.min(Math.round(products.scrollLeft), Math.ceil(maxScroll)));
        control.style.setProperty('--nni-product-scroll-thumb-size', `${thumbSize}px`);
    }

    /**
     * @param {Element} mediaHost
     */
    #bindHotspot(mediaHost) {
        const wrap = mediaHost.querySelector('.nni-hotspot-wrap');
        const trigger = mediaHost.querySelector('.nni-hotspot');
        if (!(wrap instanceof HTMLElement) || !(trigger instanceof HTMLButtonElement)) return;

        /**
         * @param {boolean} open
         */
        const setOpen = (open) => {
            trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
            if (open) this.#positionHotspotTooltip(wrap, mediaHost);
        };

        setOpen(false);

        const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

        if (canHover) {
            wrap.addEventListener('mouseenter', () => setOpen(true));
            wrap.addEventListener('mouseleave', () => setOpen(false));
        }

        trigger.addEventListener('click', (event) => {
            event.stopPropagation();
            if (canHover && event.detail > 0) return;
            setOpen(trigger.getAttribute('aria-expanded') !== 'true');
        });

        trigger.addEventListener('keydown', (event) => {
            if (event.key !== 'Escape') return;
            setOpen(false);
        });
    }

    /**
     * @param {HTMLElement} wrap
     * @param {Element} mediaHost
     */
    #positionHotspotTooltip(wrap, mediaHost) {
        const tooltip = wrap.querySelector('.nni-hotspot__tooltip');
        const container = mediaHost.querySelector('.nni-room-media');
        if (!(tooltip instanceof HTMLElement) || !(container instanceof HTMLElement)) return;

        const componentStyles = getComputedStyle(this);
        const gap = parseFloat(componentStyles.getPropertyValue('--gap-xs')) || 8;
        const padding = parseFloat(componentStyles.getPropertyValue('--padding-2xs')) || 8;
        const containerRect = container.getBoundingClientRect();
        const wrapRect = wrap.getBoundingClientRect();
        const placeOnLeft = wrapRect.left + wrapRect.width / 2 > containerRect.left + containerRect.width / 2;

        tooltip.style.top = '50%';
        tooltip.style.bottom = 'auto';
        tooltip.style.maxWidth = `${Math.max(0, Math.floor(containerRect.width - padding * 2))}px`;
        tooltip.style.marginLeft = '';
        tooltip.style.marginRight = '';
        tooltip.style.transform = 'translateY(-50%)';

        if (placeOnLeft) {
            tooltip.style.left = 'auto';
            tooltip.style.right = '100%';
            tooltip.style.marginRight = `${gap}px`;
        } else {
            tooltip.style.left = '100%';
            tooltip.style.right = 'auto';
            tooltip.style.marginLeft = `${gap}px`;
        }

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                const tooltipRect = tooltip.getBoundingClientRect();
                let translateX = 0;
                let translateY = 0;

                if (tooltipRect.right > containerRect.right - padding) {
                    translateX += containerRect.right - padding - tooltipRect.right;
                }
                if (tooltipRect.left + translateX < containerRect.left + padding) {
                    translateX += containerRect.left + padding - (tooltipRect.left + translateX);
                }
                if (tooltipRect.bottom > containerRect.bottom - padding) {
                    translateY += containerRect.bottom - padding - tooltipRect.bottom;
                }
                if (tooltipRect.top + translateY < containerRect.top + padding) {
                    translateY += containerRect.top + padding - (tooltipRect.top + translateY);
                }

                tooltip.style.transform =
                    translateX === 0 && translateY === 0
                        ? 'translateY(-50%)'
                        : `translate(${translateX}px, calc(-50% + ${translateY}px))`;
            });
        });
    }

    /**
     * @param {Element} productsHost
     */
    #bindProductRows(productsHost) {
        productsHost.querySelectorAll('.nni-product-row').forEach((row) => {
            row.querySelectorAll('input[type="radio"]').forEach((input) => {
                input.addEventListener('change', () => {
                    if (!(input instanceof HTMLInputElement)) return;
                    this.#selectVariant(row, Number(input.dataset.variantId));
                    this.#renderTotals();
                });
            });
        });
    }

    /**
     * @param {Element} row
     * @param {number} variantId
     */
    #selectVariant(row, variantId) {
        const data = this.#productData(row);
        const variant = data?.variants.find((item) => item.id === variantId);
        if (!data || !variant) return;
        data.selectedId = variant.id;
        const script = row.querySelector('.nni-product-row__data');
        if (script) script.textContent = JSON.stringify(data);

        const image = row.querySelector('.nni-product-row__image');
        if (image instanceof HTMLImageElement && variant.image) this.#swapProductImage(image, variant.image);

        const productUrl = row.getAttribute('data-product-url');
        if (productUrl) {
            const selectedProductUrl = new URL(productUrl, document.baseURI);
            selectedProductUrl.searchParams.set('variant', String(variant.id));
            const previewThemeId = new URL(window.location.href).searchParams.get('preview_theme_id');
            if (previewThemeId) selectedProductUrl.searchParams.set('preview_theme_id', previewThemeId);
            const selectedProductPath = `${selectedProductUrl.pathname}${selectedProductUrl.search}${selectedProductUrl.hash}`;
            row.querySelectorAll('a').forEach((link) => link.setAttribute('href', selectedProductPath));
        }

        const price = row.querySelector('.nni-product-row__price');
        const compare = row.querySelector('.nni-product-row__compare');
        const badge = row.querySelector('.nni-product-row__badge');
        if (price) {
            price.textContent = this.#money(variant.price);
            if (variant.discount > 0) price.setAttribute('data-sale', '');
            else price.removeAttribute('data-sale');
        }
        if (compare instanceof HTMLElement) {
            compare.hidden = variant.discount <= 0;
            const strike = compare.querySelector('s');
            if (strike) strike.textContent = this.#money(variant.compare);
        }
        if (badge instanceof HTMLElement) {
            badge.hidden = variant.discount <= 0;
            if (variant.discount > 0) badge.textContent = `${this.#money(variant.discount)} OFF`;
        }
        row.setAttribute('data-available', variant.available ? 'true' : 'false');
    }

    /**
     * @param {HTMLImageElement} image
     * @param {string} source
     */
    #swapProductImage(image, source) {
        const resolvedSource = new URL(source, document.baseURI).href;
        if (image.currentSrc === resolvedSource || image.dataset.pendingSrc === resolvedSource) return;

        image.dataset.pendingSrc = resolvedSource;
        const nextImage = new Image();
        nextImage.decoding = 'async';

        const applySource = () => {
            if (image.dataset.pendingSrc !== resolvedSource) return;
            image.removeAttribute('srcset');
            image.removeAttribute('sizes');
            image.src = resolvedSource;
            image.dataset.pendingSrc = '';
            this.#restartEntrance(image);
        };

        nextImage.addEventListener('load', applySource, { once: true });
        nextImage.addEventListener(
            'error',
            () => {
                if (image.dataset.pendingSrc === resolvedSource) image.dataset.pendingSrc = '';
            },
            { once: true }
        );
        nextImage.src = resolvedSource;
        if (nextImage.complete) applySource();
    }

    #renderTotals() {
        let roomPrice = 0;
        let currentTotal = 0;

        this.querySelectorAll('[data-nni-products] .nni-product-row').forEach((row) => {
            const variant = this.#selectedVariant(row);
            if (!variant) return;

            const rowCompare = variant.discount > 0 ? variant.compare : variant.price;
            roomPrice += rowCompare;
            currentTotal += variant.price;
        });

        const savings = Math.max(0, roomPrice - currentTotal);
        const roundedSavings = Math.ceil(savings / 100) * 100;
        const priceEl = this.querySelector('[data-nni-room-price]');
        const savingsEl = this.querySelector('[data-nni-savings]');
        const savingsRow = this.querySelector('[data-nni-savings-row]');
        const saveBadge = this.querySelector('[data-nni-save-badge]');
        const cta = this.querySelector('[data-nni-cta]');
        const ctaLabel = this.querySelector('[data-nni-cta-label]');
        const ctaPrice = this.querySelector('[data-nni-cta-price]');
        const saveTemplate = this.dataset.saveOffTemplate || 'SAVE __AMOUNT__ OFF';

        if (priceEl) priceEl.textContent = this.#money(roomPrice);
        if (savingsEl) savingsEl.textContent = savings > 0 ? this.#money(roundedSavings) : '';
        if (savingsRow instanceof HTMLElement) savingsRow.hidden = savings <= 0;
        if (saveBadge instanceof HTMLElement) {
            saveBadge.hidden = savings <= 0;
            saveBadge.textContent = saveTemplate.replace('__AMOUNT__', this.#money(roundedSavings));
        }
        if (ctaLabel) ctaLabel.textContent = this.dataset.shopLabel || '';
        if (ctaPrice) ctaPrice.textContent = currentTotal > 0 ? ` | ${this.#money(currentTotal)}` : '';
        if (cta instanceof HTMLButtonElement) cta.disabled = !this.#canShopRoom();
    }

    #canShopRoom() {
        const rows = Array.from(this.querySelectorAll('[data-nni-products] .nni-product-row'));
        if (!rows.length) return false;
        return rows.every((row) => this.#selectedVariant(row)?.available);
    }

    #selectedItems() {
        /** @type {{ id: string, quantity: number }[]} */
        const items = [];
        this.querySelectorAll('[data-nni-products] .nni-product-row').forEach((row) => {
            const variant = this.#selectedVariant(row);
            if (variant?.available) items.push({ id: String(variant.id), quantity: 1 });
        });
        return items;
    }

    /**
     * @param {Element} row
     * @returns {NurseryProductData | null}
     */
    #productData(row) {
        const script = row.querySelector('.nni-product-row__data');
        if (!script?.textContent) return null;
        try {
            return JSON.parse(script.textContent);
        } catch (error) {
            console.error(error);
            return null;
        }
    }

    /**
     * @param {Element} row
     * @returns {NurseryVariant | undefined}
     */
    #selectedVariant(row) {
        const data = this.#productData(row);
        return data?.variants.find((variant) => variant.id === data.selectedId) || data?.variants[0];
    }

    /**
     * @param {number} cents
     */
    #money(cents) {
        return formatShopMoney(cents);
    }

    /**
     * @returns {Promise<{ item_count: number }>}
     */
    async #fetchCart() {
        const response = await fetch(`${Theme.routes.cart_url}.js`, {
            headers: { Accept: 'application/json' },
        });
        if (!response.ok) throw new Error(`Cart fetch failed: ${response.status}`);
        return response.json();
    }

    #applyDeepLink() {
        const params = new URLSearchParams(window.location.search);
        const style = params.get('style');
        if (!style) return;
        this.#activeFilter = style;
        this.#syncFilterButtons();
    }

    /** @returns {DialogComponent | null} */
    #dialogHost() {
        const host = this.querySelector('dialog-component');
        return host instanceof DialogComponent ? host : null;
    }

    /** @param {Event} event */
    #onBlockSelect = (event) => {
        const target = event.target;
        if (!(target instanceof HTMLElement)) return;
        if (!this.contains(target)) return;
        const room = target.closest('.nni__room');
        if (room instanceof HTMLElement) this.#openRoom(room);
    };

    #onBlockDeselect = () => {
        this.#dialogHost()?.closeDialog();
    };

    #onDialogClosed = () => {
        const mediaHost = this.querySelector('[data-nni-media]');
        mediaHost?.replaceChildren();
        mediaHost?.removeAttribute('aria-busy');
        this.querySelector('[data-nni-products]')?.replaceChildren();
        const productScroll = this.querySelector('[data-nni-product-scroll]');
        if (productScroll instanceof HTMLInputElement) {
            productScroll.hidden = true;
            productScroll.value = '0';
        }
    };

    /** @param {KeyboardEvent} event */
    #onKeyDown = (event) => {
        const dialog = this.#dialogHost()?.querySelector('dialog');
        if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;
        if (
            event.target instanceof HTMLElement &&
            event.target.closest('input, select, textarea, [contenteditable="true"]')
        ) {
            return;
        }
        if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.showPreviousRoom();
        }
        if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.showNextRoom();
        }
    };
}

if (!customElements.get('na-nursery-inspiration-component')) {
    customElements.define('na-nursery-inspiration-component', NurseryInspirationComponent);
}
